import { createSelector, createSlice } from "@reduxjs/toolkit";
import { startCase } from "lodash";

import { GenerateCodeParams, ValidateCodeParams } from "src/interfaces";
import { translate } from "src/services";
import {
  generateCode as doSendCode,
  validateCode as doValidateCode,
  validateVendorURL as doValidateVendorURL,
  logOut as doLogOut,
  getUserCookie,
} from "../services";
import { AppDispatch, AppState } from "../store";

const cookieUser = getUserCookie();

const NAME = "auth";

const initialState = {
  vendorSettings: null,
  vendorURL: cookieUser?.vendorUrl || (null as string | null),
  isAuthenticated: !!cookieUser,
  isLoading: false,
  isSendingCode: false,
  user: { phone: "", email: "", ...cookieUser },
  error: null as string | null,
};

const authSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    setVendorURL: (state, action) => {
      state.vendorURL = action.payload;
    },
    startSendingCode: (state) => {
      state.isSendingCode = true;
    },
    completeSendingCode: (state) => {
      state.isSendingCode = false;
    },
    startValidatingCode: (state) => {
      state.isLoading = true;
    },
    completeValidatingCode: (state) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.error = null;
    },
    failValidatingCode: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    logOut: (state) => {
      state.isAuthenticated = false;
    },
    setPhone: (state, action) => {
      state.user.phone = action.payload;
    },
    setEmail: (state, action) => {
      state.user.email = action.payload;
    },
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
  },
});

export const setVendorURL = (vendorURL: string) => (dispatch: AppDispatch) => {
  dispatch(authSlice.actions.setVendorURL(vendorURL));
};

export const removeVendorURL = () => (dispatch: AppDispatch) => {
  dispatch(authSlice.actions.setVendorURL(null));
};

export const sendCode =
  (params: GenerateCodeParams) => (dispatch: AppDispatch) => {
    dispatch(authSlice.actions.startSendingCode());
    const promise = doSendCode(params);
    promise
      .then(() => {
        dispatch(authSlice.actions.completeSendingCode());
      })
      .catch(() => {
        dispatch(authSlice.actions.completeSendingCode());
      });
    return promise;
  };

export const validateCode =
  (params: ValidateCodeParams) => (dispatch: AppDispatch) => {
    dispatch(authSlice.actions.startValidatingCode());
    const promise = doValidateCode(params);
    promise
      .then(() => {
        dispatch(authSlice.actions.completeValidatingCode());
      })
      .catch((e) => {
        const { data } = e.response;
        const errorCode = data ? data.code : null;
        let errorMessage = "";
        if (errorCode === "CustomerBulkySchedulerInvalidCode")
          errorMessage = translate("alertMessages.codeDoesNotMatch");

        dispatch(authSlice.actions.failValidatingCode(errorMessage));
      });
    return promise;
  };

export const validateVendorURL =
  (vendorUrl: string) => (dispatch: AppDispatch) => {
    const promise = doValidateVendorURL(vendorUrl);
    promise
      .then((isValid) => {
        if (isValid) {
          setVendorURL(vendorUrl)(dispatch);
        } else {
          logOut()(dispatch);
        }
      })
      .catch(() => {
        logOut()(dispatch);
      });
    return promise;
  };

export const logOut = () => (dispatch: AppDispatch) => {
  dispatch(authSlice.actions.logOut());
  doLogOut();
};

const vendorUrlSelector = (state: AppState) => state.auth.vendorURL;

export const formattedVendorUrlSelector = createSelector(
  vendorUrlSelector,
  (vendorUrl) => {
    if (!vendorUrl) {
      return null;
    }
    return startCase(vendorUrl);
  }
);

export default authSlice.reducer;

export const { setPhone, setEmail, setIsAuthenticated } = authSlice.actions;
